//react
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";

// Services and utils
import { COUNTRY_OPTIONS, getCountriesAsSelectOptions } from "../../../utils/constants/countryOptions";
import {
  getSimpleLineConfig,
  GRID_CONFIG,
  GRID_CONFIG_WITH_LEGEND,
  TOOLTIP_STYLE
} from "../../../utils/constants/graphConfigs";

// Components
import CardContainer from "../../reusable/CardContainer";
import CenterSpin from "../../misc/spin";
import CardOnlyYear from "../../reusable/CardOnlyYear";
import CardError from "../../reusable/CardError/CardError";

// Texts
import { migNews as texts_es } from "../../../utils/text/graphTexts";
import { migNews as texts_en } from "../../../utils/text/graphTexts_en";
import miscText from "../../../utils/text/misc.json";
import { generateLineToolTip, generateMultiplLineTooltip } from "../../../utils/functions/generateLineTooltip";
import { getDateString } from "../../../utils/functions/getDateString";
import { getPreviousMonth } from "../../../utils/functions/getPreviousMonth";
import { seriesValues } from "../../../utils/functions/seriesValues";
import { transformNameCountries } from "../../../utils/functions/transformNameCountries";
import { isCountryIncluded } from "../../../utils/functions/isCountryIncluded";
import { handleYearChange } from "../../../utils/functions/handleYearChange";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";
import { getMonthlyNewsCoverage, getNewsYearlyCoverage } from "../../../services/singleMonths";
import { getNewsCoverageData } from "../../../services/dashboard";

export default function News({ selectedCountry, colorRBG }) {
  const currentCardType = "news";
  const { country } = useParams();
  const { language } = useCurrentLanguage();
  const { dateOptions } = useContextData();

  // State
  const [currentCountry, setCurrentCountry] = useState(selectedCountry ?? country);
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry || opt.code === country)
  );
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [options, setOptions] = useState();
  const [selectedYear, setSelectedYear] = useState();
  const [lastMonthData, setLastMonthData] = useState();
  const [isMostRecentYear, setIsMostRecentYear] = useState(true);
  const [change, setChange] = useState();
  const [countrySelectConfig, setCountrySelectConfig] = useState({
    isIncluded: currentCountry === "LAC",
    value: ["LAC"],
    options: getCountriesAsSelectOptions(language),
  });

  const createDateString = () => {
    try {
      return isMostRecentYear
        ? getDateString(language, dateOptions.mostRecentDate.year, dateOptions.mostRecentDate.month)
        : getDateString(language, selectedYear === "HIST" ? dateOptions.mostRecentDate.year : selectedYear);
    } catch {
      return "-----";
    }
  };

  function handleCountrySelectChange(newValue) {
    setCountrySelectConfig({
      ...countrySelectConfig,
      value: newValue,
    });
  }

  useEffect(() => {
    if (dateOptions) {
      setSelectedYear("HIST");
      setIsLoadingInitialData(false);
      setCountryProps(COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry));
    }
  }, [dateOptions, selectedCountry]);

  useEffect(() => {
    async function updateData() {
      try {
        setIsLoading(true);
        setOptions(null);
        
        const response = await getNewsCoverageData(currentCountry, selectedYear);
        
        if (response) {
          const transformedDatesData = transformNameCountries(response.data, language);
          let countries = Object.keys(transformedDatesData);
          countries.shift();
          
          let series = seriesValues(transformedDatesData);
          
          if (country === "LAC") {
            countries = countries.filter((c) =>
              isCountryIncluded(c, countrySelectConfig.value, language)
          );
          
          series = series.filter((line) =>
            isCountryIncluded(line.name, countrySelectConfig.value, language)
        );
      }
      
      const options = {
        ...getSimpleLineConfig(
          currentCountry,
          countries,
          colorRBG,
          transformedDatesData.dates,
          true,
          true,
          language
        ),
        grid: selectedCountry === "LAC" ? GRID_CONFIG_WITH_LEGEND : GRID_CONFIG,
        series: series,
        tooltip: {
          trigger: "axis",
          ...TOOLTIP_STYLE,
          formatter: function (params) {
            const isPercentage = true;
            const areColorsSwitched = false;
            if (currentCountry === "LAC") {
              return generateMultiplLineTooltip(
                params,
                transformedDatesData,
                isPercentage,
                areColorsSwitched,
                language
              );
            } else {
              return generateLineToolTip(
                params,
                "news",
                selectedYear,
                transformedDatesData,
                dateOptions.yearOptions,
                isPercentage,
                language
              );
            }
          },
        },
      };
      
      setOptions(options);
      setIsDataAvailable(true);
      setErrorLoadingGraph(false);
    }
  } catch {
    setOptions(null);
    setErrorLoadingGraph(true);
    setIsDataAvailable(false);
  } finally {
    setIsLoading(false);
  }
}

if (!isLoadingInitialData && dateOptions && selectedYear) updateData();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  dateOptions,
    selectedYear,
    isLoadingInitialData,
    currentCountry,
    countrySelectConfig,
    colorRBG,
    language,
  ]);


  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      const isFirstYearSelected = selectedYear === dateOptions.firstYear;
      
      let promises;
      let callback;
      
      if (isMostRecentYear) {
        const previousMonth = getPreviousMonth(
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month,
          dateOptions.yearOptions
        );
        
        const currentMonthPromise = getMonthlyNewsCoverage(
          currentCountry,
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month
        );
        
        const previousMonthPromise = getMonthlyNewsCoverage(
          currentCountry,
          previousMonth.year,
          previousMonth.month
        );
        
        promises = [currentMonthPromise, previousMonthPromise];
        callback = (results) => {
          const diff = results[0].data.value - results[1].data.value;
          setChange(diff.toFixed(1));
          setLastMonthData(results[0].data.value.toFixed(1));
        };
      } else if (isFirstYearSelected) {
        const newsYearlyPromise = getNewsYearlyCoverage(currentCountry, selectedYear);
        promises = [newsYearlyPromise];
        callback = (results) => {
          const avgCurrentYear = results[0].data.value;
          setLastMonthData(avgCurrentYear.toFixed(1));
          setChange("--");
        };
      } else {
        const currentYearPromise = getNewsYearlyCoverage(currentCountry, selectedYear);
        const previousYearPromise = getNewsYearlyCoverage(currentCountry, parseInt(selectedYear) - 1);
        
        promises = [currentYearPromise, previousYearPromise];
        callback = (results) => {
          const avgCurrentYear = results[0].data.value;
          const avgPreviousYear = results[1].data.value;
          const diff = avgCurrentYear - avgPreviousYear;
          
          setChange(diff.toFixed(1));
          setLastMonthData(avgCurrentYear.toFixed(1));
        };
      }
      
      Promise.all(promises)
      .then(callback)
      .catch(() => {
        setChange("--");
        setLastMonthData("--");
      })
      .finally(() => {
        setIsLoading(false);
      });
    };
    
    if (!isLoadingInitialData && dateOptions) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, isLoadingInitialData, selectedYear, currentCountry, isMostRecentYear]);
  
  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountrySelectConfig({
        ...countrySelectConfig,
        isIncluded: selectedCountry === "LAC",
      });
      setCountryProps(COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
    setCountrySelectConfig({
      ...countrySelectConfig,
      options: getCountriesAsSelectOptions(language),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  function NewsCardContent() {
    try {
      return (
        <CardOnlyYear
          countryProps={countryProps}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          options={options}
          selectedYear={selectedYear}
          lastMonthData={lastMonthData ? lastMonthData + "%" : "--"}
          dataDate={createDateString()}
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description(
            isMostRecentYear,
            isMostRecentYear ? dateOptions.mostRecentDate.month : selectedYear,
            lastMonthData,
            change
          )}
          selectArray={[
            {
              selectedValue: selectedYear,
              changeHandler: (newSelectedYear) =>
                handleYearChange(
                  newSelectedYear,
                  setIsMostRecentYear,
                  setSelectedYear,
                  dateOptions
                ),
              options: dateOptions.yearOptions,
              placeHolderText: miscText["yearPlaceholder"][language],
              labelAttributeName: "years_list",
              valueAttributeName: "years_code",
            },
          ]}
          language={language}
          includesCountrySelect={countrySelectConfig.isIncluded}
          handleCountrySelectChange={handleCountrySelectChange}
          countrySelectDefaultValue={countrySelectConfig.value}
          countrySelectOptions={countrySelectConfig.options}
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es" ? "PorcentajeDeNoticias" : "NewsPercentage",
            currentCountry,
            selectedYear
          )}
          downloadableCardTitle={texts.boxTitle}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language
          )}
          downloadableCardContainsLegend={currentCountry === "LAC"}
        />
      );
    } catch {
      return (
        <CardError
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }
  }

  return (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <NewsCardContent />}
    </CardContainer>
  );
}

